import { z } from 'zod';
import { IdSchema, SlugSchema, SipDomainSchema, TimestampSchema, ListQuerySchema } from './common.js';

// Matches the CHECK constraint on tenants.status exactly. 'pending' was never
// a database value -- a tenant starts as 'provisioning'.
export const TenantStatusSchema = z.enum(['provisioning', 'active', 'suspended', 'closed']);
export const BillingModeSchema = z.enum(['prepaid', 'postpaid']);

export const TenantSchema = z.object({
  id: IdSchema,
  reseller_id: IdSchema.nullable(),
  name: z.string(),
  slug: SlugSchema,
  sip_domain: SipDomainSchema,
  status: TenantStatusSchema,
  timezone: z.string(),
  country_code: z.string().length(2),
  currency: z.string().length(3),
  billing_mode: BillingModeSchema,
  max_concurrent_calls: z.number().int(),
  max_cps: z.number().int(),
  max_extensions: z.number().int().optional(),
  call_plan_id: IdSchema.nullable().optional(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  version: z.number().int(),
});
export type Tenant = z.infer<typeof TenantSchema>;

/**
 * sip_domain is absent by design. It is permanent once set -- a tenant's phones
 * are configured with it, so changing it de-registers every handset. The
 * database enforces this with a trigger (arc.forbid_sip_domain_change); leaving
 * it out of the update DTO means the API never even offers the mistake.
 */
export const TenantUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  timezone: z.string().min(1).max(64).optional(),
  country_code: z.string().length(2).optional(),
  billing_mode: BillingModeSchema.optional(),
  max_concurrent_calls: z.number().int().min(0).max(10000).optional(),
  max_cps: z.number().int().min(0).max(1000).optional(),
}).strict();
export type TenantUpdate = z.infer<typeof TenantUpdateSchema>;

export const TenantCreateSchema = z.object({
  name: z.string().min(1).max(200),
  slug: SlugSchema,
  sip_domain: SipDomainSchema,
  reseller_id: IdSchema.optional(),
  timezone: z.string().min(1).max(64).default('Africa/Johannesburg'),
  country_code: z.string().length(2).default('ZA'),
  currency: z.string().length(3).default('ZAR'),
  billing_mode: BillingModeSchema.default('prepaid'),
  max_concurrent_calls: z.number().int().min(0).max(10000).default(20),
  max_cps: z.number().int().min(0).max(1000).default(5),
}).strict();
export type TenantCreate = z.infer<typeof TenantCreateSchema>;

export const TenantListQuerySchema = ListQuerySchema.extend({
  status: TenantStatusSchema.optional(),
  reseller_id: IdSchema.optional(),
  q: z.string().max(200).optional(),
  sort: z.enum(['name', '-name', 'created_at', '-created_at']).default('-created_at'),
});

export const SiteSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  timezone: z.string().nullable(),
  address: z.string().nullable(),
  created_at: TimestampSchema,
});
export type Site = z.infer<typeof SiteSchema>;

export const ResellerSchema = z.object({
  id: IdSchema,
  name: z.string(),
  slug: SlugSchema,
  status: z.enum(['active', 'suspended']),
  currency: z.string().length(3),
  created_at: TimestampSchema,
});
export type Reseller = z.infer<typeof ResellerSchema>;
